<template>
  <div class="management-order-monitor-page">
    <div class="table-container">
      <div class="table-header">
        <h3>Order Monitor</h3>
        <div class="header-actions">
          <!-- Search 功能 -->
          <div class="search-container">
            <el-input
              v-model="searchKeyword"
              placeholder="搜索订单号/客户/船名/港口..."
              size="small"
              style="width: 220px;"
              clearable
              @keyup.enter="handleSearch"
              @clear="handleSearch"
            >
              <template #prefix>
                <el-icon><Search /></el-icon>
              </template>
            </el-input>
            <el-button size="small" type="primary" @click="handleSearch">
              <el-icon><Search /></el-icon> Search
            </el-button>
          </div>

          <!-- Sorting 功能 -->
          <div class="sorting-container">
            <span class="sorting-label">
              <el-icon><Sort /></el-icon>
              Sorting
            </span>
            <div class="sorting-fields">
              <div 
                v-for="(item, index) in sortFields" 
                :key="index"
                class="sorting-field-item"
              >
                <span class="sort-num">{{ index + 1 }}</span>
                <el-select 
                  v-model="item.field" 
                  placeholder="字段"
                  size="small"
                  style="width: 50px;"
                >
                  <el-option 
                    v-for="col in sortableColumns" 
                    :key="col.prop"
                    :label="col.label" 
                    :value="col.prop"
                    :disabled="isFieldUsed(item.field, index)"
                  />
                </el-select>
                <el-select 
                  v-model="item.order" 
                  placeholder="顺序"
                  size="small"
                  style="width: 40px; margin-left: 4px;"
                >
                  <el-option label="A" value="ascending" />
                  <el-option label="D" value="descending" />
                </el-select>
              </div>
            </div>
            <div class="sorting-actions-inline">
              <el-button 
                size="small" 
                type="primary" 
                plain
                style="padding: 2px 12px; font-size: 11px;"
                @click="applySort"
              >
                Start
              </el-button>
              <el-button 
                size="small" 
                type="danger" 
                plain
                style="padding: 2px 8px; font-size: 11px;"
                @click="clearSort"
              >
                Clear
              </el-button>
            </div>
          </div>

          <!-- Row Setting 按钮 -->
          <el-popover
            placement="bottom-end"
            width="auto"
            trigger="click"
          >
            <template #reference>
              <el-button size="small" type="primary" plain>
                <el-icon><Setting /></el-icon>
                Row Setting
              </el-button>
            </template>
            <div class="column-setting-horizontal">
              <div 
                v-for="col in visibleColumns" 
                :key="col.prop"
                class="column-setting-item-horizontal"
              >
                <el-checkbox 
                  v-model="col.visible" 
                  :label="col.label"
                  size="small"
                />
              </div>
            </div>
          </el-popover>

          <el-button size="small" type="success" plain @click="loadOrderList">
            <el-icon><Refresh /></el-icon> Refresh
          </el-button>
        </div>
      </div>
      <el-table 
        :data="filteredData" 
        border 
        style="width: 100%" 
        max-height="600"
        v-loading="loading"
        :row-style="{ height: 'auto' }"
        :cell-style="{ padding: '0px 2px' }"
        :resizable="false"
      >
        <el-table-column type="index" label="No" width="40" align="center" />
        
        <!-- Operator 列 -->
        <el-table-column 
          prop="operator" 
          label="Operator" 
          width="80"
          v-if="getColumnVisible('operator')"
        >
          <template #default="{ row }">
            <span class="cell-text">{{ row.operator || '-' }}</span>
          </template>
        </el-table-column>
        
        <el-table-column 
          prop="orderNo" 
          label="Order No" 
          width="80"
          v-if="getColumnVisible('orderNo')"
        >
          <template #default="{ row }">
            <span class="cell-text">{{ row.orderNo }}</span>
          </template>
        </el-table-column>
        
        <el-table-column 
          prop="customer" 
          label="Customer" 
          width="100"
          v-if="getColumnVisible('customer')"
        >
          <template #default="{ row }">
            <span class="cell-text cell-left">{{ row.customer }}</span>
          </template>
        </el-table-column>
        
        <el-table-column 
          prop="vessel" 
          label="Vessel" 
          width="130"
          v-if="getColumnVisible('vessel')"
        >
          <template #default="{ row }">
            <span class="cell-text cell-left">{{ row.vessel }}</span>
          </template>
        </el-table-column>
        
        <el-table-column 
          prop="imo" 
          label="IMO" 
          width="60"
          v-if="getColumnVisible('imo')"
        >
          <template #default="{ row }">
            <span class="cell-text cell-left">{{ row.imo }}</span>
          </template>
        </el-table-column>
        
        <el-table-column 
          prop="type" 
          label="Type" 
          width="70"
          v-if="getColumnVisible('type')"
        >
          <template #default="{ row }">
            <span class="cell-text cell-left">{{ row.type }}</span>
          </template>
        </el-table-column>
        
        <el-table-column 
          prop="port" 
          label="Port" 
          width="90"
          v-if="getColumnVisible('port')"
        >
          <template #default="{ row }">
            <span class="cell-text cell-left">{{ row.port }}</span>
          </template>
        </el-table-column>
        
        <!-- ETB - 只显示月日时分 -->
        <el-table-column 
          prop="etb" 
          label="ETB" 
          width="80"
          v-if="getColumnVisible('etb')"
        >
          <template #default="{ row }">
            <span class="cell-text">{{ formatDateTimeShort(row.etb) }}</span>
          </template>
        </el-table-column>
        
        <!-- ETD - 只显示月日时分 -->
        <el-table-column 
          prop="etd" 
          label="ETD" 
          width="80"
          v-if="getColumnVisible('etd')"
        >
          <template #default="{ row }">
            <span class="cell-text">{{ formatDateTimeShort(row.etd) }}</span>
          </template>
        </el-table-column>
        
        <el-table-column 
          prop="pWindow" 
          label="P Window" 
          width="80"
          v-if="getColumnVisible('pWindow')"
        >
          <template #default="{ row }">
            <span class="cell-text">{{ formatDateOnly(row.pWindow) }}</span>
          </template>
        </el-table-column>
        
        <el-table-column 
          prop="rLevel" 
          label="R Level" 
          width="70"
          v-if="getColumnVisible('rLevel')"
        >
          <template #default="{ row }">
            <span class="cell-text">{{ row.rLevel || '-' }}</span>
          </template>
        </el-table-column>
        
        <!-- SPRO & Amt - 合并 -->
        <el-table-column 
          prop="spro" 
          label="SPRO&amp;Amt" 
          width="80"
          v-if="getColumnVisible('spro')"
        >
          <template #default="{ row }">
            <div class="merged-cell-two">
              <span class="status-cell" :class="getStatusClass(row.spro)">
                {{ row.spro || 'N' }}
              </span>
              <span class="cell-text amount-text">{{ row.sproAmt }}</span>
            </div>
          </template>
        </el-table-column>
        
        <!-- Sludge & Amt & SQty - 合并 -->
        <el-table-column 
          prop="sludge" 
          label="Sludge &amp; Amt &amp; SQty" 
          width="130"
          v-if="getColumnVisible('sludge')"
        >
          <template #default="{ row }">
            <div class="merged-cell-three">
              <span class="status-cell" :class="getStatusClass(row.sludge)">
                {{ row.sludge || 'N' }}
              </span>
              <span class="cell-text amount-text">{{ row.sludgeAmt }}</span>
              <span class="cell-text qty-text">{{ row.sludgeQty ?? '-' }}</span>
            </div>
          </template>
        </el-table-column>
        
        <!-- Garbage & Amt & GQty - 合并 -->
        <el-table-column 
          prop="garbage" 
          label="Garbage &amp; Amt &amp; GQty" 
          width="135"
          v-if="getColumnVisible('garbage')"
        >
          <template #default="{ row }">
            <div class="merged-cell-three">
              <span class="status-cell" :class="getStatusClass(row.garbage)">
                {{ row.garbage || 'N' }}
              </span>
              <span class="cell-text amount-text">{{ row.garbageAmt }}</span>
              <span class="cell-text qty-text">{{ row.garbageQty ?? '-' }}</span>
            </div>
          </template>
        </el-table-column>
        
        <!-- MC & Amt - 合并 -->
        <el-table-column 
          prop="mc" 
          label="MC &amp; Amt" 
          width="80"
          v-if="getColumnVisible('mc')"
        >
          <template #default="{ row }">
            <div class="merged-cell-two">
              <span class="status-cell" :class="getStatusClass(row.mc)">
                {{ row.mc || 'N' }}
              </span>
              <span class="cell-text amount-text">{{ row.mcAmt }}</span>
            </div>
          </template>
        </el-table-column>
        
        <!-- Others & Amt - 合并 -->
        <el-table-column 
          prop="others" 
          label="Others &amp; Amt" 
          width="80"
          v-if="getColumnVisible('others')"
        >
          <template #default="{ row }">
            <div class="merged-cell-two">
              <span class="status-cell" :class="getStatusClass(row.others)">
                {{ row.others || 'N' }}
              </span>
              <span class="cell-text amount-text">{{ row.othersAmt }}</span>
            </div>
          </template>
        </el-table-column>
        
        <!-- INV Amt - 移到 Others Amt 后面 -->
        <el-table-column 
          prop="invAmount" 
          label="INV Amt" 
          width="60"
          v-if="getColumnVisible('invAmount')"
        >
          <template #default="{ row }">
            <span class="cell-text inv-amount-text">{{ row.invAmount }}</span>
          </template>
        </el-table-column>
        
        <!-- Ops Remarks -->
        <el-table-column 
          prop="opsRemarks" 
          label="Ops Remarks" 
          width="130"
          v-if="getColumnVisible('opsRemarks')"
        >
          <template #default="{ row }">
            <span class="cell-text cell-left">{{ row.opsRemarks || '-' }}</span>
          </template>
        </el-table-column>
        
        <!-- MC Remarks - 新增 -->
        <el-table-column 
          prop="mcRemarks" 
          label="MC Remarks" 
          width="130"
          v-if="getColumnVisible('mcRemarks')"
        >
          <template #default="{ row }">
            <span class="cell-text cell-left">{{ row.mcRemarks || '-' }}</span>
          </template>
        </el-table-column>
        
        <!-- Fin Remarks -->
        <el-table-column 
          prop="finRemarks" 
          label="Fin Remarks" 
          width="130"
          v-if="getColumnVisible('finRemarks')"
        >
          <template #default="{ row }">
            <span class="cell-text cell-left">{{ row.finRemarks || '-' }}</span>
          </template>
        </el-table-column>
        
        <!-- Status - 新增状态列 -->
        <el-table-column 
          prop="statusDisplay" 
          label="Status" 
          width="70"
          v-if="getColumnVisible('statusDisplay')"
        >
          <template #default="{ row }">
            <el-tag :type="getStatusTagType(row.status)" size="small">
              {{ getStatusLabel(row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        
        <!-- Payment Date -->
        <el-table-column 
          prop="paymentDate" 
          label="Payment Date" 
          width="80"
          v-if="getColumnVisible('paymentDate')"
        >
          <template #default="{ row }">
            <span class="cell-text">{{ formatDateOnly(row.paymentDate) }}</span>
          </template>
        </el-table-column>
        
        <!-- Payment Ref -->
        <el-table-column 
          prop="paymentRef" 
          label="Payment Ref" 
          width="80"
          v-if="getColumnVisible('paymentRef')"
        >
          <template #default="{ row }">
            <span class="cell-text cell-left">{{ row.paymentRef || '-' }}</span>
          </template>
        </el-table-column>
        
        <!-- Payment Amt -->
        <el-table-column 
          prop="paymentAmt" 
          label="Payment Amt" 
          width="80"
          v-if="getColumnVisible('paymentAmt')"
        >
          <template #default="{ row }">
            <span class="cell-text inv-amount-text">{{ row.paymentAmt ?? '-' }}</span>
          </template>
        </el-table-column>
        
        <!-- T INV Amt - 新增 -->
        <el-table-column 
          prop="tInvAmt" 
          label="T INV Amt" 
          width="80"
          v-if="getColumnVisible('tInvAmt')"
        >
          <template #default="{ row }">
            <span class="cell-text t-inv-amount-text">{{ row.tInvAmt ?? '-' }}</span>
          </template>
        </el-table-column>
        
      </el-table>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { ElMessage } from 'element-plus'
import { Setting, Sort, Refresh, Search } from '@element-plus/icons-vue'

// 加载状态
const loading = ref(false)

// 订单列表
const orderList = ref([])

// 搜索关键词
const searchKeyword = ref('')

// 排序相关
const sortFields = ref([
  { field: '', order: 'ascending' },
  { field: '', order: 'ascending' },
  { field: '', order: 'ascending' },
  { field: '', order: 'ascending' },
  { field: '', order: 'ascending' }
])

const sortableColumns = [
  { prop: 'operator', label: 'Operator' },
  { prop: 'orderNo', label: 'Order No' },
  { prop: 'customer', label: 'Customer' },
  { prop: 'port', label: 'Port' },
  { prop: 'etb', label: 'ETB' },
  { prop: 'spro', label: 'SPRO' },
  { prop: 'sludge', label: 'Sludge' },
  { prop: 'garbage', label: 'Garbage' },
  { prop: 'mc', label: 'MC' },
  { prop: 'others', label: 'Others' },
  { prop: 'status', label: 'Status' }
]

const isFieldUsed = (currentField, currentIndex) => {
  return sortFields.value.some((item, index) => 
    index !== currentIndex && item.field === currentField && currentField !== ''
  )
}

const clearSort = () => {
  sortFields.value = [
    { field: '', order: 'ascending' },
    { field: '', order: 'ascending' },
    { field: '', order: 'ascending' },
    { field: '', order: 'ascending' },
    { field: '', order: 'ascending' }
  ]
  filteredData.value = orderList.value
  ElMessage.info('已清空排序条件')
}

// 列显示控制
const columns = ref([
  { prop: 'operator', label: 'Operator', visible: true },
  { prop: 'orderNo', label: 'Order No', visible: true },
  { prop: 'customer', label: 'Customer', visible: true },
  { prop: 'vessel', label: 'Vessel', visible: true },
  { prop: 'imo', label: 'IMO', visible: true },
  { prop: 'type', label: 'Type', visible: true },
  { prop: 'port', label: 'Port', visible: true },
  { prop: 'etb', label: 'ETB', visible: true },
  { prop: 'etd', label: 'ETD', visible: true },
  { prop: 'pWindow', label: 'P Window', visible: true },
  { prop: 'rLevel', label: 'R Level', visible: true },
  { prop: 'spro', label: 'SPRO&amp;Amt', visible: true },
  { prop: 'sludge', label: 'Sludge&amp;Amt&amp;SQty', visible: true },
  { prop: 'garbage', label: 'Garbage&amp;Amt&amp;GQty', visible: true },
  { prop: 'mc', label: 'MC&amp;Amt', visible: true },
  { prop: 'others', label: 'Others&amp;Amt', visible: true },
  { prop: 'invAmount', label: 'INV Amt', visible: true },
  { prop: 'opsRemarks', label: 'Ops Remarks', visible: true },
  { prop: 'mcRemarks', label: 'MC Remarks', visible: true },
  { prop: 'finRemarks', label: 'Fin Remarks', visible: true },
  { prop: 'statusDisplay', label: 'Status', visible: true },
  { prop: 'paymentDate', label: 'Payment Date', visible: true },
  { prop: 'paymentRef', label: 'Payment Ref', visible: true },
  { prop: 'paymentAmt', label: 'Payment Amt', visible: true },
  { prop: 'tInvAmt', label: 'T INV Amt', visible: true }
])

const visibleColumns = computed(() => columns.value)

const getColumnVisible = (prop) => {
  const col = columns.value.find(c => c.prop === prop)
  return col ? col.visible : true
}

// ============================================================
// 状态辅助函数
// ============================================================
const getStatusLabel = (status) => {
  const map = {
    'pending': '🟡 运营中',
    'submitted': '🔵 已提交财务',
    'confirmed': '🟢 财务确认中',
    'completed': '✅ 已完成'
  }
  return map[status] || status || '运营中'
}

const getStatusTagType = (status) => {
  const map = {
    'pending': 'warning',
    'submitted': 'primary',
    'confirmed': 'success',
    'completed': 'success'
  }
  return map[status] || 'info'
}

// ============================================================
// 状态样式类
// ============================================================
const getStatusClass = (value) => {
  if (value === 'P') return 'status-p'
  if (value === 'A') return 'status-a'
  return 'status-n'
}

// ============================================================
// 工具函数 - ETB/ETD 只显示月日时分
// ============================================================
const formatDateTimeShort = (dateStr) => {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  if (isNaN(d.getTime())) return ''
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  const hours = String(d.getHours()).padStart(2, '0')
  const minutes = String(d.getMinutes()).padStart(2, '0')
  return `${month}-${day} ${hours}:${minutes}`
}

const formatDateOnly = (dateStr) => {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  if (isNaN(d.getTime())) return ''
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

// ============================================================
// 搜索逻辑
// ============================================================
const filteredData = ref([])

const handleSearch = () => {
  const keyword = searchKeyword.value.trim().toLowerCase()
  if (!keyword) {
    filteredData.value = orderList.value
    return
  }
  
  filteredData.value = orderList.value.filter(item => {
    return (
      (item.orderNo && item.orderNo.toLowerCase().includes(keyword)) ||
      (item.customer && item.customer.toLowerCase().includes(keyword)) ||
      (item.vessel && item.vessel.toLowerCase().includes(keyword)) ||
      (item.port && item.port.toLowerCase().includes(keyword)) ||
      (item.operator && item.operator.toLowerCase().includes(keyword)) ||
      (item.invNo && item.invNo.toLowerCase().includes(keyword))
    )
  })
  
  if (filteredData.value.length === 0) {
    ElMessage.info('未找到匹配的订单')
  }
}

// ============================================================
// 排序逻辑
// ============================================================
const applySort = () => {
  const activeSorts = sortFields.value.filter(item => item.field !== '')
  if (activeSorts.length === 0) {
    filteredData.value = orderList.value
    ElMessage.info('没有选择排序字段，显示原始数据')
    return
  }
  
  const sorted = [...filteredData.value]
  sorted.sort((a, b) => {
    for (const sortItem of activeSorts) {
      const field = sortItem.field
      const order = sortItem.order
      
      let valA = a[field]
      let valB = b[field]
      
      if (valA === null || valA === undefined || valA === '') valA = ''
      if (valB === null || valB === undefined || valB === '') valB = ''
      
      if (typeof valA === 'number' && typeof valB === 'number') {
        if (valA !== valB) {
          return order === 'ascending' ? valA - valB : valB - valA
        }
        continue
      }
      
      valA = String(valA).toLowerCase()
      valB = String(valB).toLowerCase()
      
      if (valA !== valB) {
        if (order === 'ascending') {
          return valA.localeCompare(valB)
        } else {
          return valB.localeCompare(valA)
        }
      }
    }
    return 0
  })
  filteredData.value = sorted
  ElMessage.success(`已按 ${activeSorts.length} 个字段排序`)
}

// ========== API 配置 ==========
const getApiBase = () => {
  return process.env.VUE_APP_API_URL || 'http://127.0.0.1:3000'
}

const getToken = () => {
  return localStorage.getItem('token')
}

const getHeaders = () => {
  const token = getToken()
  return {
    'Content-Type': 'application/json',
    'Authorization': token ? `Bearer ${token}` : ''
  }
}

// ========== API 接口 - 使用新的完整数据接口 ==========

const fetchOrders = async () => {
  try {
    // ✅ 使用新的完整数据接口，同时查询 order_monitoring 和 finance_monitoring
    const response = await fetch(`${getApiBase()}/api/auth/order-monitoring-full`, {
      headers: getHeaders()
    })
    if (!response.ok) {
      if (response.status === 401) {
        ElMessage.error('认证已过期，请重新登录')
        return []
      }
      throw new Error('获取订单列表失败')
    }
    return await response.json()
  } catch (error) {
    console.error('获取订单列表错误:', error)
    throw error
  }
}

// ========== 业务逻辑 ==========

const mapDbToFrontend = (dbData) => {
  // ✅ 判断是否有财务数据（finance_monitoring 关联存在）
  const hasFinanceData = dbData.finance_id !== null && dbData.finance_id !== undefined;
  
  return {
    id: dbData.id,
    no: dbData.no,
    operator: dbData.operator || '',
    orderNo: dbData.order_no || '',
    customer: dbData.customer || '',
    vessel: dbData.vessel || '',
    imo: dbData.imo || '',
    type: dbData.type || '',
    port: dbData.port || '',
    etb: dbData.etb || '',
    etd: dbData.etd || '',
    pWindow: dbData.p_window || '',
    rLevel: dbData.r_level || '',
    spro: dbData.spro || 'N',
    sproAmt: dbData.spro_amt ?? 0,
    sludge: dbData.sludge || 'N',
    sludgeAmt: dbData.sludge_amt ?? 0,
    sludgeQty: dbData.s_qty ?? null,
    garbage: dbData.garbage || 'N',
    garbageAmt: dbData.garbage_amt ?? 0,
    garbageQty: dbData.g_qty ?? null,
    mc: dbData.mc || 'N',
    mcAmt: dbData.mc_amt ?? 0,
    mcRemarks: dbData.mc_remarks || '',
    others: dbData.others || 'N',
    othersAmt: dbData.others_amt ?? 0,
    opsRemarks: dbData.ops_remarks || '',
    invAmount: dbData.inv_amount ?? 0,
    // ✅ 从 finance_monitoring 获取财务字段
    finRemarks: hasFinanceData ? (dbData.fin_remarks || '') : '',
    paymentDate: hasFinanceData ? (dbData.payment_date || '') : '',
    paymentRef: hasFinanceData ? (dbData.payment_ref || '') : '',
    paymentAmt: hasFinanceData ? (dbData.ref_amt ?? null) : null,
    // ✅ 计算 T INV Amt
    tInvAmt: hasFinanceData ? ((dbData.inv_amount ?? 0) - (dbData.ref_amt ?? 0)) : null,
    // ✅ 状态：优先使用 finance_monitoring 的状态
    status: hasFinanceData ? dbData.finance_status : dbData.status,
    // 保留原始数据用于调试
    _raw: dbData
  }
}

const loadOrderList = async () => {
  loading.value = true
  try {
    const data = await fetchOrders()
    if (data && Array.isArray(data)) {
      orderList.value = data.map(item => mapDbToFrontend(item))
      filteredData.value = orderList.value
      console.log('📋 加载订单数据成功:', orderList.value.length, '条')
    } else {
      orderList.value = []
      filteredData.value = []
    }
  } catch (error) {
    ElMessage.error('加载订单列表失败')
    orderList.value = []
    filteredData.value = []
  } finally {
    loading.value = false
  }
}

// ========== 页面初始化 ==========
onMounted(() => {
  loadOrderList()
})
</script>

<style scoped>
.management-order-monitor-page {
  padding: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #f5f7fa;
}

.table-container {
  flex: 1;
  overflow: auto;
  background: white;
  border-radius: 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.06);
  padding: 2px 3px;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
  padding-bottom: 2px;
  border-bottom: 2px solid #ebeef5;
}

.table-header h3 {
  margin: 0;
  font-size: 18px;
  color: #303133;
  font-weight: 600;
}

.header-actions {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
}

/* Search 样式 */
.search-container {
  display: flex;
  align-items: center;
  gap: 4px;
  background: #f8f9fa;
  padding: 2px 6px;
  border-radius: 6px;
  border: 1px solid #e8ecf1;
}

.search-container .el-input {
  width: 220px;
}

.search-container .el-input :deep(.el-input__wrapper) {
  height: 28px !important;
  min-height: 28px !important;
}

.search-container .el-input :deep(.el-input__inner) {
  font-size: 12px !important;
  height: 26px !important;
  line-height: 26px !important;
}

.sorting-container {
  display: flex;
  align-items: center;
  gap: 6px;
  background: #f8f9fa;
  padding: 4px 10px;
  border-radius: 6px;
  border: 1px solid #e8ecf1;
}

.sorting-label {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 600;
  color: #e6a23c;
  white-space: nowrap;
}

.sorting-fields {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
}

.sorting-field-item {
  display: flex;
  align-items: center;
  gap: 2px;
  background: white;
  padding: 2px 4px;
  border-radius: 4px;
  border: 1px solid #e8ecf1;
}

.sort-num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  background: #409eff;
  color: white;
  border-radius: 50%;
  font-size: 10px;
  font-weight: 600;
  flex-shrink: 0;
}

.sorting-actions-inline {
  display: flex;
  align-items: center;
  gap: 2px;
  margin-left: 2px;
}

.sorting-actions-inline .el-button {
  height: 22px;
  line-height: 22px;
}

.column-setting-horizontal {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 12px;
  padding: 12px 16px;
  max-width: 600px;
  min-width: 300px;
}

.column-setting-item-horizontal {
  flex: 0 0 auto;
  white-space: nowrap;
}

.cell-text {
  display: block;
  padding: 0px 2px;
  color: #303133;
  font-weight: 400;
  font-size: 10px;
  text-align: center;
  width: 100%;
  min-height: 18px;
  line-height: 18px;
}

.cell-left {
  text-align: left !important;
}

.amount-text {
  color: #409eff;
  font-weight: 500;
  background: #ecf5ff;
  border-radius: 3px;
}

.inv-amount-text {
  color: #67c23a;
  font-weight: 700;
  background: #f0f9eb;
  border-radius: 3px;
}

.t-inv-amount-text {
  color: #f56c6c;
  font-weight: 700;
  background: #fef0f0;
  border-radius: 3px;
}

.qty-text {
  color: #909399;
  font-weight: 400;
  background: #f5f7fa;
  font-size: 10px;
  border-radius: 3px;
}

/* 合并单元格样式 */
.merged-cell-two {
  display: flex;
  align-items: center;
  gap: 2px;
  width: 100%;
}

.merged-cell-two .status-cell {
  flex-shrink: 0;
}

.merged-cell-two .cell-text {
  flex: 1;
  min-width: 30px;
}

.merged-cell-three {
  display: flex;
  align-items: center;
  gap: 2px;
  width: 100%;
}

.merged-cell-three .status-cell {
  flex-shrink: 0;
}

.merged-cell-three .cell-text {
  flex: 1;
  min-width: 30px;
}

/* ============================================================
   状态样式 - P（红色）, A（绿色）, N（灰色）
   ============================================================ */
.status-cell {
  display: inline-block;
  padding: 0px 6px;
  border-radius: 3px;
  font-size: 10px;
  font-weight: 700;
  min-width: 20px;
  text-align: center;
}

/* P 状态 - 中国红 */
.status-p {
  background-color: #de2910 !important;
  color: #ffffff !important;
  font-weight: 700 !important;
}

/* A 状态 - 亮绿色 */
.status-a {
  background-color: #07c160 !important;
  color: #ffffff !important;
  font-weight: 700 !important;
}

/* N 状态 - 灰色 */
.status-n {
  background-color: #f5f5f5 !important;
  color: #999999 !important;
  font-weight: 400 !important;
}

:deep(.el-table) {
  font-size: 12px;
}

:deep(.el-table .cell) {
  padding: 0px 2px !important;
  line-height: 16px !important;
  min-height: 24px !important;
}

:deep(.el-table td.el-table__cell) {
  padding: 1px 0px !important;
}

:deep(.el-table th.el-table__cell) {
  background-color: #3498db !important;
  color: #ffffff !important;
  padding: 0px 0px !important;
}

:deep(.el-table th.el-table__cell .cell) {
  line-height: 26px !important;
  font-size: 10px !important;
  color: #ffffff !important;
}

:deep(.el-table__body-wrapper) {
  overflow-y: auto;
}

:deep(.el-table__header-wrapper) {
  background: #f5f7fa;
}

.el-table th.el-table__cell .cell {
  white-space: normal;
  word-break: break-word;
  text-align: center;
  font-size: 10px !important;
}

.table-container::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.table-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.table-container::-webkit-scrollbar-thumb {
  background: #c1c7cd;
  border-radius: 4px;
}

.table-container::-webkit-scrollbar-thumb:hover {
  background: #a8b0b8;
}

.sorting-field-item .el-input__inner {
  font-size: 11px !important;
  height: 24px !important;
  line-height: 24px !important;
}

.sorting-field-item .el-input__wrapper {
  height: 24px !important;
  min-height: 24px !important;
  padding: 0 4px !important;
}

/* Status 标签样式 */
:deep(.el-tag) {
  font-size: 10px !important;
  height: 22px !important;
  line-height: 20px !important;
  padding: 0 8px !important;
}
</style>